import { useState, useEffect } from "react";
import { FaFilePdf, FaTrash, FaCheck, FaTimesCircle, FaDownload, FaTimes, FaExclamationTriangle, FaInfoCircle } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../../../context/SocketContext";

const Invoice = () => {
  const { currentThemeColors } = useTheme();
  const [invoices, setInvoices] = useState([]);
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm] = useState("");
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const navigate = useNavigate();

  useEffect(() => {
    const fetchInvoices = async () => {
      const token = localStorage.getItem("token");
      if (!token) {
        navigate("/login");
        return;
      }

      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/user`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!response.ok) throw new Error("Failed to fetch user data");

        const data = await response.json();
        // Reverse the invoices array to show latest first
        const invoicesReversed = (data.user.invoices || []).reverse();
        setInvoices(invoicesReversed);
        setFilteredInvoices(invoicesReversed);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    fetchInvoices();
  }, [navigate]);

  useEffect(() => {
    const filtered = invoices.filter(invoice =>
      (invoice.originalName || `Invoice`).toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredInvoices(filtered);
  }, [searchTerm, invoices]);

  const handleDownload = async (invoice) => {
    try {
      // If there's no fileUrl, it means the invoice was cancelled or not prepared
      if (!invoice.fileUrl) {
        setSnackbar({
          open: true,
          message: "Invoice is not available for download",
          severity: "warning"
        });
        return;
      }

      // Open in new tab for download
      window.open(`${process.env.REACT_APP_API_BASE_URL}${invoice.fileUrl?.replace(/http:\/\/localhost:5000\//, '/')}`, '_blank');

      // Mark invoice as downloaded
      const token = localStorage.getItem("token");
      if (!token) {
        navigate("/login");
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/invoice/mark-downloaded`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({ invoiceId: invoice._id })
      });

      if (response.ok) {
        // Update local state to show invoice as downloaded
        setInvoices(prevInvoices => 
          prevInvoices.map(inv => 
            inv._id === invoice._id ? {...inv, downloaded: true} : inv
          )
        );
      }
    } catch (error) {
      console.error("Error downloading invoice:", error);
    }
  };

  const handleCancelInvoice = (invoice) => {
    setSelectedInvoice(invoice);
    setCancelDialogOpen(true);
  };

  const confirmCancelInvoice = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        navigate("/login");
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/invoice/cancel-invoice`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({ 
          paymentId: selectedInvoice.paymentId,
          reason: cancelReason 
        })
      });

      if (response.ok) {
        // Update local state to show invoice as cancelled
        const updatedInvoice = { 
          ...selectedInvoice, 
          status: 'cancelled', 
          fileUrl: null 
        };

        // Add the cancelled invoice to the list and refresh the view
        setInvoices(prevInvoices => [...prevInvoices, updatedInvoice]);
        
        setSnackbar({
          open: true,
          message: "Invoice cancelled successfully",
          severity: "success"
        });
      } else {
        setSnackbar({
          open: true,
          message: "Failed to cancel invoice",
          severity: "error"
        });
      }
    } catch (error) {
      console.error("Error cancelling invoice:", error);
      setSnackbar({
        open: true,
        message: "Error cancelling invoice",
        severity: "error"
      });
    } finally {
      setCancelDialogOpen(false);
      setCancelReason("");
      setSelectedInvoice(null);
    }
  };

  const closeCancelDialog = () => {
    setCancelDialogOpen(false);
    setCancelReason("");
    setSelectedInvoice(null);
  };

  const closeSnackbar = (_event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };

  const getStatusIcon = (invoice) => {
    if (invoice.status === 'cancelled') {
      return <FaTimesCircle className="text-red-500" title="Cancelled" />;
    } else if (invoice.downloaded) {
      return <FaCheck className="text-green-500" title="Downloaded" />;
    }
    return null;
  };

  // Helper to format bytes to KB/MB
  const formatFileSize = (bytes) => {
    if (!bytes || bytes <= 0) return '-';
    const kb = bytes / 1024;
    if (kb < 1024) return `${Math.round(kb)} KB`;
    const mb = kb / 1024;
    return `${mb.toFixed(2)} MB`;
  };

  const getStatusBadge = (invoice) => {
    if (invoice.status === 'cancelled') {
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400">
          <FaTimesCircle className="w-3 h-3" />
          Cancelled
        </span>
      );
    } else if (invoice.downloaded) {
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">
          <FaCheck className="w-3 h-3" />
          Downloaded
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400">
        Available
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="card p-6 opacity-0 animate-fade-in-up" style={{ animationDelay: '0ms', animationFillMode: 'forwards' }}>
        <div className="flex items-center gap-4">
          <div 
            className="w-14 h-14 rounded-2xl flex items-center justify-center shadow-lg transition-transform duration-300 hover:scale-110 hover:rotate-6"
            style={{ 
              background: `linear-gradient(135deg, var(--accent-500), var(--accent-600))`,
              boxShadow: `0 10px 15px -3px var(--glow-color)`
            }}
          >
            <FaFilePdf className="w-7 h-7 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-dark-800 dark:text-white">My Invoices</h1>
            <p className="text-gray-500 dark:text-gray-400">Download and manage your invoices</p>
          </div>
        </div>
      </div>

      {/* Information Note */}
      <div 
        className="p-4 rounded-xl border-l-4 opacity-0 animate-fade-in-up" 
        style={{ 
          animationDelay: '100ms', 
          animationFillMode: 'forwards',
          background: 'var(--primary-50)',
          borderColor: 'var(--primary-200)',
          borderLeftColor: 'var(--primary-500)'
        }}
      >
        <div className="flex items-start gap-3">
          <FaInfoCircle className="w-5 h-5 text-primary-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-primary-700 dark:text-primary-300">
            <strong>Note:</strong> Invoices are displayed here only for payments made directly through the portal. 
            If you made a payment outside the portal, please request your invoice by contacting{' '}
            <a href="mailto:contact@bookscomply.com" className="font-medium underline hover:text-primary-800 dark:hover:text-primary-200">
              contact@bookscomply.com
            </a>.
          </p>
        </div>
      </div>

      {/* Invoice Content */}
      {loading ? (
        <div className="card p-12">
          <div className="flex flex-col items-center justify-center">
            <div className="w-12 h-12 border-4 border-primary-200 dark:border-primary-800 border-t-primary-500 rounded-full animate-spin mb-4" />
            <p className="text-gray-600 dark:text-gray-400">Loading invoices...</p>
          </div>
        </div>
      ) : (
        <div className="card overflow-hidden">
          {/* Desktop Table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 dark:bg-dark-700 border-b border-gray-200 dark:border-dark-600">
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Invoice Name</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Size</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Date & Time</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-dark-600">
                {filteredInvoices.length > 0 ? (
                  filteredInvoices.map((invoice, index) => (
                    <tr key={index} className="hover:bg-gray-50 dark:hover:bg-dark-700/50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <FaFilePdf className="w-5 h-5 text-red-500 flex-shrink-0" />
                          <span className="text-sm font-medium text-dark-800 dark:text-white truncate max-w-xs">
                            {invoice.originalName || `Invoice ${index + 1}`}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">
                        {formatFileSize(invoice.size)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">
                        {new Date(invoice.date).toLocaleString()}
                      </td>
                      <td className="px-6 py-4">
                        {getStatusBadge(invoice)}
                      </td>
                      <td className="px-6 py-4 text-right">
                        {invoice.status !== 'cancelled' ? (
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => handleDownload(invoice)}
                              disabled={!invoice.fileUrl}
                              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20 hover:bg-primary-100 dark:hover:bg-primary-900/40 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                              <FaDownload className="w-4 h-4" />
                              Download
                            </button>
                            {!invoice.fileUrl && (
                              <button
                                onClick={() => handleCancelInvoice(invoice)}
                                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors"
                              >
                                <FaTrash className="w-3.5 h-3.5" />
                                Cancel
                              </button>
                            )}
                          </div>
                        ) : (
                          <span className="text-sm text-gray-500 dark:text-gray-400">Invoice cancelled</span>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5} className="px-6 py-12 text-center">
                      <FaFilePdf className="w-12 h-12 text-gray-300 dark:text-dark-600 mx-auto mb-3" />
                      <p className="text-gray-500 dark:text-gray-400">No invoices available</p>
                    </td>
                  </tr>
                )}
              </tbody>
              <tfoot>
                <tr className="bg-gray-50 dark:bg-dark-700 border-t border-gray-200 dark:border-dark-600">
                  <td colSpan={5} className="px-6 py-3 text-right text-sm font-medium text-gray-600 dark:text-gray-400">
                    Total: {filteredInvoices.length} invoice{filteredInvoices.length !== 1 ? 's' : ''}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden p-4 space-y-3">
            {filteredInvoices.length > 0 ? (
              filteredInvoices.map((invoice, index) => (
                <div key={index} className="p-4 rounded-xl bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600">
                  <div className="flex items-start gap-3 mb-3">
                    <FaFilePdf className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-dark-800 dark:text-white truncate">
                        {invoice.originalName || `Invoice ${index + 1}`}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {new Date(invoice.date).toLocaleString()}
                      </p>
                    </div>
                    {getStatusBadge(invoice)}
                  </div>
                  <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mb-3">
                    <span>Size: {formatFileSize(invoice.size)}</span>
                  </div>
                  {invoice.status !== 'cancelled' ? (
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleDownload(invoice)}
                        disabled={!invoice.fileUrl}
                        className="flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20 hover:bg-primary-100 dark:hover:bg-primary-900/40 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        <FaDownload className="w-4 h-4" />
                        Download
                      </button>
                      {!invoice.fileUrl && (
                        <button
                          onClick={() => handleCancelInvoice(invoice)}
                          className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors"
                        >
                          <FaTrash className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-red-500">Invoice cancelled</p>
                  )}
                </div>
              ))
            ) : (
              <div className="p-8 text-center">
                <FaFilePdf className="w-12 h-12 text-gray-300 dark:text-dark-600 mx-auto mb-3" />
                <p className="text-gray-500 dark:text-gray-400">No invoices available</p>
              </div>
            )}
            <p className="text-right text-sm text-gray-500 dark:text-gray-400 pt-2">
              Total: {filteredInvoices.length} invoice{filteredInvoices.length !== 1 ? 's' : ''}
            </p>
          </div>
        </div>
      )}

      {/* Cancel Invoice Modal */}
      {cancelDialogOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-2xl w-full max-w-md animate-scale-in">
            <div className="p-6 border-b border-gray-100 dark:border-dark-700">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                  <FaExclamationTriangle className="w-6 h-6 text-red-500" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-dark-800 dark:text-white">Cancel Invoice</h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400">This action cannot be undone</p>
                </div>
              </div>
            </div>
            <div className="p-6">
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Are you sure you want to cancel this invoice?
              </p>
              <div className="space-y-2">
                <label className="block text-sm font-medium text-dark-700 dark:text-gray-300">
                  Reason for cancellation (optional)
                </label>
                <textarea
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  placeholder="Enter reason..."
                  rows={3}
                  className="w-full p-3 rounded-xl bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 text-dark-800 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500 transition-all duration-200 resize-none"
                />
              </div>
            </div>
            <div className="p-6 border-t border-gray-100 dark:border-dark-700 flex gap-3">
              <button
                onClick={closeCancelDialog}
                className="flex-1 px-4 py-2.5 rounded-xl font-medium text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-dark-700 hover:bg-gray-200 dark:hover:bg-dark-600 transition-colors"
              >
                Back
              </button>
              <button
                onClick={confirmCancelInvoice}
                className="flex-1 px-4 py-2.5 rounded-xl font-medium text-white bg-red-500 hover:bg-red-600 transition-colors"
              >
                Confirm Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Snackbar/Toast */}
      {snackbar.open && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 animate-slide-up">
          <div className={`flex items-center gap-3 px-4 py-3 rounded-xl shadow-lg ${
            snackbar.severity === 'success' ? 'bg-green-500' : 
            snackbar.severity === 'error' ? 'bg-red-500' : 'bg-yellow-500'
          } text-white`}>
            {snackbar.severity === 'success' ? <FaCheck className="w-4 h-4" /> : 
             snackbar.severity === 'error' ? <FaTimesCircle className="w-4 h-4" /> : 
             <FaExclamationTriangle className="w-4 h-4" />}
            <span className="text-sm font-medium">{snackbar.message}</span>
            <button onClick={() => setSnackbar({ ...snackbar, open: false })} className="ml-2 hover:opacity-80">
              <FaTimes className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Invoice;